import { useState, useEffect } from 'react'
import Card, { CardHeader } from '../ui/Card'
import Badge from '../ui/Badge'
import SectionLabel from '../ui/SectionLabel'
import LoadingBear from '../ui/LoadingBear'
import { fetchSparrowOps } from '../../api/client'

export default function SparrowOpsPanel() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSparrowOps().then(setData).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingBear message="Sparrow on comms..." />

  return (
    <div>
      <SectionLabel>Panel 8 — Sparrow Ops</SectionLabel>

      <Card delay={0.05}>
        <CardHeader title="Sparrow Ops" badge="🐦 On duty" badgeVariant="persimmon" />

        <div className="grid grid-cols-2 gap-5">
          {/* Baseline Tracker */}
          <div className="col-span-2 bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              7-Day Baseline Tracker
            </h4>
            <BaselineGrid data={data?.baseline ?? []} />
          </div>

          {/* Stickers */}
          <div className="bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              Sticker Count — Today
            </h4>
            <div className="space-y-4">
              {[
                { emoji: '⭐', label: 'Standard', count: data?.stickers?.standard ?? 0 },
                { emoji: '🌟', label: 'Silver', count: data?.stickers?.silver ?? 0 },
                { emoji: '🏅', label: 'Gold Star', count: data?.stickers?.gold ?? 0 },
                { emoji: '🏆', label: 'Trophy', count: data?.stickers?.trophy ?? 0 },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">{s.emoji}</span>
                    <span className="text-xs text-mm-parchment/50">{s.label}</span>
                  </div>
                  <span className="font-glacial-bold text-2xl text-mm-gold">{s.count}</span>
                </div>
              ))}
              <div className="flex items-center gap-3 p-3.5 bg-mm-parchment/[0.08] rounded-lg mt-2">
                <span className="font-glacial-bold text-4xl text-mm-sage">{data?.streak ?? 0}</span>
                <span className="text-xs text-mm-parchment/50 leading-snug">
                  Day streak<br />with at least one 🏅
                </span>
              </div>
            </div>
          </div>

          {/* Task Completion */}
          <div className="bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              Task Completion Rate
            </h4>
            <div className="space-y-1.5">
              {(data?.tasks ?? []).map(t => (
                <div key={t.date} className="flex items-center gap-3 py-1.5">
                  <span className="text-xs text-mm-parchment/50 w-14 text-right">{t.date}</span>
                  <div className="flex-1 h-2 bg-mm-border rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-mm-sage to-mm-sage/50"
                      style={{ width: `${t.pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-glacial-bold text-mm-gold w-9">{t.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Flags for Embers */}
          <div className="col-span-2 bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              Recent Flags for Embers
            </h4>
            <div className="space-y-2.5">
              {(data?.flags ?? []).map((flag, i) => (
                <div key={i} className="flex gap-3 p-3 bg-mm-parchment/[0.08] rounded-lg border-l-[3px] border-mm-persimmon">
                  <div>
                    <div className="text-[10px] text-mm-parchment/40 mb-1">{flag.date}</div>
                    <p className="text-xs text-mm-parchment/85 leading-relaxed">{flag.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

function BaselineGrid({ data }) {
  const rows = ['Meds', 'Meals', 'Water', 'Movement']
  const dates = data.map(d => d.date)

  const cellClass = (status) => {
    if (status === true) return 'bg-mm-sage/30 text-mm-sage'
    if (status === false) return 'bg-mm-persimmon/30 text-mm-persimmon'
    return 'bg-mm-parchment/5 text-mm-parchment/40'
  }

  const cellIcon = (status) => {
    if (status === true) return '✓'
    if (status === false) return '!'
    return '?'
  }

  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: `auto repeat(${dates.length}, 1fr)` }}>
      {/* Header */}
      <div />
      {dates.map(d => (
        <div key={d} className="text-[10px] text-mm-parchment/40 text-center pb-1.5">{d}</div>
      ))}

      {/* Rows */}
      {rows.map(row => (
        <>
          <div key={`label-${row}`} className="text-xs text-mm-parchment pr-3 flex items-center">{row}</div>
          {data.map((day, i) => (
            <div
              key={`${row}-${i}`}
              className={`aspect-square rounded flex items-center justify-center text-xs min-h-[32px] ${cellClass(day[row.toLowerCase()])}`}
            >
              {cellIcon(day[row.toLowerCase()])}
            </div>
          ))}
        </>
      ))}
    </div>
  )
}
